import React, { useRef } from 'react';
import '../../assets/css/ai/IlamAI.css';

const IlamAI = () => {
  const featuresRef = useRef(null);

  const scrollToFeatures = () => {
    featuresRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="ai-ilam-ai-page">
      {/* 1. Hero Section */}
      <section className="ai-hero-section">
        <div className="ai-hero-particles"></div>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="ai-hero-content">
                <div className="ai-hero-badge">✨ POWERED BY ARTIFICIAL INTELLIGENCE</div>
                <h1 className="ai-hero-title">
                  Ilam AI
                  <span className="ai-gradient-text">Smart Learning Assistant</span>
                </h1>
                <p className="ai-hero-subtitle">
                  Transform lengthy study notes into clear, concise summaries in seconds. 
                  Let AI do the heavy lifting while you focus on learning.
                </p>
                <div className="ai-hero-buttons">
                  <button className="ai-btn-primary-ai" onClick={scrollToFeatures}>
                    <i className="fas fa-magic"></i>
                    Summarize My Notes
                  </button>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="ai-hero-image-wrapper">
                <img 
                  src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800" 
                  alt="AI Illustration" 
                  className="ai-hero-ai-image"
                />
                <div className="ai-floating-icon ai-icon-1">🧠</div>
                <div className="ai-floating-icon ai-icon-2">📚</div>
                <div className="ai-floating-icon ai-icon-3">✨</div>
                <div className="ai-floating-icon ai-icon-4">🎯</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. What is Ilam AI */}
      <section className="ai-what-is-section" ref={featuresRef}>
        <div className="container">
          <div className="ai-section-intro">
            <h2 className="ai-section-title">What is Ilam AI?</h2>
            <p className="ai-section-description">
              Ilam AI is your intelligent study companion that uses advanced artificial intelligence 
              to transform complex notes into easy-to-understand summaries. Designed specifically for 
              students, Ilam AI helps you save time, improve comprehension, and study more effectively. 
              Whether you're preparing for exams or reviewing lectures, our AI analyzes your content 
              and delivers key insights instantly.
            </p>
          </div>
        </div>
      </section>

      {/* 3. Key Features */}
      <section className="ai-key-features-section">
        <div className="container">
          <div className="ai-section-header text-center">
  
            <h2 className="ai-section-title">Key Features</h2>
          </div>

          <div className="row g-4">
            {/* Feature 1 */}
            <div className="col-lg-3 col-md-6">
              <div className="ai-feature-card">
                <div className="ai-feature-icon-wrapper">
                  <i className="fas fa-brain"></i>
                </div>
                <h4>AI Summarization</h4>
                <p>Advanced algorithms analyze and condense your notes while preserving key concepts.</p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="col-lg-3 col-md-6">
              <div className="ai-feature-card">
                <div className="ai-feature-icon-wrapper">
                  <i className="fas fa-list-ul"></i>
                </div>
                <h4>Key Points Extraction</h4>
                <p>Automatically identifies and highlights the most important information from your content.</p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="col-lg-3 col-md-6">
              <div className="ai-feature-card">
                <div className="ai-feature-icon-wrapper">
                  <i className="fas fa-clock"></i>
                </div>
                <h4>Time Saving</h4>
                <p>Reduce hours of reading into minutes of focused review and efficient learning.</p>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="col-lg-3 col-md-6">
              <div className="ai-feature-card">
                <div className="ai-feature-icon-wrapper">
                  <i className="fas fa-check-circle"></i>
                </div>
                <h4>Easy to Understand</h4>
                <p>Summaries are written in clear, simple language that's easy to comprehend and remember.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. How Ilam AI Works */}
      <section className="ai-how-it-works-section">
        <div className="container">
          <div className="ai-section-header text-center">
            <h2 className="ai-section-title">How Ilam AI Works</h2>
        
          </div>

          <div className="ai-steps-container">
            {/* Step 1 */}
            <div className="ai-step-item">
              <div className="ai-step-number">01</div>
              <div className="ai-step-icon">
                <i className="fas fa-upload"></i>
              </div>
              <h4>Upload Notes</h4>
              <p>Upload your lecture notes, PDFs, or text documents directly to the platform.</p>
            </div>

            <div className="ai-step-connector"></div>

            {/* Step 2 */}
            <div className="ai-step-item">
              <div className="ai-step-number">02</div>
              <div className="ai-step-icon">
                <i className="fas fa-cogs"></i>
              </div>
              <h4>AI Analyzes Content</h4>
              <p>Our AI engine processes and understands the context of your material.</p>
            </div>

            <div className="ai-step-connector"></div>

            {/* Step 3 */}
            <div className="ai-step-item">
              <div className="ai-step-number">03</div>
              <div className="ai-step-icon">
                <i className="fas fa-file-alt"></i>
              </div>
              <h4>Generates Summary</h4>
              <p>Receive a concise, well-structured summary highlighting key points.</p>
            </div>

            <div className="ai-step-connector"></div>

            {/* Step 4 */}
            <div className="ai-step-item">
              <div className="ai-step-number">04</div>
              <div className="ai-step-icon">
                <i className="fas fa-share-alt"></i>
              </div>
              <h4>Review & Share</h4>
              <p>Review your summary and share it with your study group instantly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. AI in Group Study */}
      <section className="ai-group-study-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800" 
                alt="Group Study" 
                className="ai-group-study-image"
              />
            </div>
            <div className="col-lg-6">
              <div className="ai-group-study-content">

                <h2 className="ai-section-title">AI in Group Study</h2>
                <p className="ai-section-description">
                  Ilam AI revolutionizes collaborative learning by enabling seamless sharing 
                  of AI-generated summaries within your study circles. When one member creates 
                  a summary, the entire group benefits instantly.
                </p>
                <div className="ai-benefit-list">
                  <div className="ai-benefit-item">
                    <i className="fas fa-users"></i>
                    <span>Share summaries with entire study group</span>
                  </div>
                  <div className="ai-benefit-item">
                    <i className="fas fa-sync"></i>
                    <span>Real-time collaboration and feedback</span>
                  </div>
                  <div className="ai-benefit-item">
                    <i className="fas fa-comments"></i>
                    <span>Discuss and refine summaries together</span>
                  </div>
                  <div className="ai-benefit-item">
                    <i className="fas fa-star"></i>
                    <span>Build a shared knowledge repository</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Benefits for Students */}
      <section className="ai-benefits-section">
        <div className="container">
          <div className="ai-section-header text-center">
            <h2 className="ai-section-title">Benefits for Students</h2>
          </div>

          <div className="row g-4">
            <div className="col-lg-4 col-md-6">
              <div className="ai-benefit-card">
                <div className="ai-benefit-icon">⏰</div>
                <h4>Save Time</h4>
                <p>Cut study hours in half by focusing only on essential information and key concepts.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6">
              <div className="ai-benefit-card">
                <div className="ai-benefit-icon">😌</div>
                <h4>Reduce Study Stress</h4>
                <p>Eliminate overwhelm with organized, digestible summaries that make learning manageable.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6">
              <div className="ai-benefit-card">
                <div className="ai-benefit-icon">📈</div>
                <h4>Improve Exam Preparation</h4>
                <p>Boost retention and performance with structured, easy-to-review study materials.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Future of Ilam AI */}
      <section className="ai-future-section">
        <div className="container">
          <div className="ai-section-header text-center">
         
            <h2 className="ai-section-title">Future of Ilam AI</h2>
         
          </div>

          <div className="row g-4">
            <div className="col-lg-4 col-md-6">
              <div className="ai-future-card">
                <div className="ai-future-icon">
                  <i className="fas fa-question-circle"></i>
                </div>
                <h4>AI Quiz Generation</h4>
                <p>Automatically create practice quizzes from your summaries to test your knowledge.</p>
                <span className="ai-coming-soon-badge">Coming Soon</span>
              </div>
            </div>

            <div className="col-lg-4 col-md-6">
              <div className="ai-future-card">
                <div className="ai-future-icon">
                  <i className="fas fa-microphone"></i>
                </div>
                <h4>Voice Summaries</h4>
                <p>Listen to your summaries on the go with AI-powered text-to-speech narration.</p>
                <span className="ai-coming-soon-badge">Coming Soon</span>
              </div>
            </div>

            <div className="col-lg-4 col-md-6">
              <div className="ai-future-card">
                <div className="ai-future-icon">
                  <i className="fas fa-globe"></i>
                </div>
                <h4>Multi-Language Support</h4>
                <p>Generate summaries in multiple languages for international students.</p>
                <span className="ai-coming-soon-badge">Coming Soon</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Final CTA */}
      <section className="ai-final-cta-section">
        <div className="container">
          <div className="ai-cta-wrapper">
            <div className="ai-cta-content">
              <h2 className="ai-cta-title">Study Smarter, Not Harder with Ilam AI</h2>
              <p className="ai-cta-description">
                Join thousands of students who are already using AI to enhance their learning experience.
              </p>
            </div>
            <div className="ai-cta-decoration">
              <div className="ai-decoration-circle ai-circle-1"></div>
              <div className="ai-decoration-circle ai-circle-2"></div>
              <div className="ai-decoration-circle ai-circle-3"></div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IlamAI;